/**
 * ClubSignup — public membership application form.
 *
 * Path: /club/signup
 * Used on tenants where `allow_signup: true` (lesconcierges today, not KVB).
 *
 * Submits the application via POST /api/club/signup → backend creates
 * a `status=pending_review` member + pushes a Zoho ticket. Admin reviews,
 * allocates a card number, and emails the customer via Zoho.
 *
 * FIR-20260611-001 · sign-up flow
 */

import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ClubLayout from "./ClubLayout";
import * as api from "./api";
import CldImg from "@/components/CldImg";

const NEW_DEFAULTS = {
  first_name: "",
  last_name: "",
  email: "",
  mobile: "",
  address: "",
  resident_city: "",
  resident_state: "",
  resident_pincode: "",
  spouse_name: "",
  children_names: [],
  tier: "",
  terms_accepted: false,
  // FIR-20260612-007 · DPDP Act 2023 consent fields, both pre-ticked
  // by default per founder direction (12 Feb 2026). Founder noted on
  // record that pre-ticking the REQUIRED consent is sub-optimal under
  // DPDP §6(1)'s "clear affirmative action" standard — easy to flip
  // the defaults to false later if a Data Protection Board challenge
  // ever surfaces.
  dpdp_consent: true,
  marketing_consent: true,
  // FIR-20260623 · founder requested photo upload on signup form
  photo_url: "",
  spouse_photo_url: "",
};

export default function ClubSignup() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState(NEW_DEFAULTS);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [childInput, setChildInput] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const cfg = await api.getConfig();
        setConfig(cfg);
        if (!cfg.allow_signup) {
          // Tenant doesn't allow sign-up → bounce back to the gate.
          navigate("/club", { replace: true });
        }
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [navigate]);

  function set(k, v) { setDraft((d) => ({ ...d, [k]: v })); }

  function addChild() {
    const v = (childInput || "").trim();
    if (!v) return;
    if ((draft.children_names || []).length >= 2) return;
    setDraft((d) => ({ ...d, children_names: [...(d.children_names || []), v] }));
    setChildInput("");
  }
  function removeChild(i) {
    setDraft((d) => ({ ...d, children_names: d.children_names.filter((_, idx) => idx !== i) }));
  }

  async function submit(e) {
    e.preventDefault();
    setError(null);
    const req = ["first_name", "last_name", "email", "mobile", "resident_city"];
    for (const k of req) {
      if (!(draft[k] || "").trim()) {
        setError(`Please fill: ${k.replace(/_/g, " ")}`);
        return;
      }
    }
    if (!draft.dpdp_consent) {
      setError("Please accept the data-protection consent (required) to apply.");
      return;
    }
    // The legacy `terms_accepted` is now derived from dpdp_consent —
    // they capture the same affirmative act.
    const payload = { ...draft, terms_accepted: !!draft.dpdp_consent };
    setSubmitting(true);
    try {
      const r = await api.signup(payload);
      setSuccess(r);
    } catch (e2) {
      const code = e2.message || "";
      if (code === "application_exists") {
        setError("An application with this email is already on file. Our team will be in touch — please don't apply twice.");
      } else if (code === "signup_not_allowed_for_tenant") {
        setError("Membership applications aren't open on this surface.");
      } else {
        setError(code || "Couldn't submit. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <ClubLayout member={null}>
        <main className="wrap" style={{ paddingTop: 60, paddingBottom: 80 }}>
          <div style={{ maxWidth: 560, margin: "20px auto 0" }} data-testid="club-signup-success">
            <div className="kicker">{config?.name || "Club Social®"} · Application received</div>
            <h1 className="serif" style={{ fontSize: "clamp(28px, 4.5vw, 44px)", marginTop: 8, lineHeight: 1.1 }}>
              Thank you, <em style={{ color: "var(--teal)", fontStyle: "italic" }}>{draft.first_name}</em>.
            </h1>
            <p style={{ color: "var(--muted)", marginTop: 14, lineHeight: 1.6 }}>
              {success.message}
            </p>
            <div style={{ marginTop: 22, padding: "16px 18px", background: "rgba(46,157,157,0.08)",
                          border: "1px solid rgba(46,157,157,0.25)", borderRadius: 12 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                            color: "var(--muted)", fontWeight: 700, marginBottom: 6 }}>
                Reference
              </div>
              <div data-testid="club-signup-ref"
                   style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 18, color: "var(--teal-d)" }}>
                {success.ref}
              </div>
            </div>
            <p style={{ marginTop: 22, color: "var(--muted)", fontSize: 13.5, lineHeight: 1.6 }}>
              Once you receive your membership card number by email, return to this page and sign in with your last
              name and card number to browse the Club Discovery Network.
            </p>
            <Link to="/club" className="btn btn-ghost" data-testid="club-signup-back"
                  style={{ marginTop: 22, textDecoration: "none", display: "inline-block", padding: "10px 20px" }}>
              ← back to sign in
            </Link>
          </div>
        </main>
      </ClubLayout>
    );
  }

  return (
    <ClubLayout member={null}>
      <main className="wrap" style={{ paddingTop: 40, paddingBottom: 80 }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }} data-testid="club-signup-form">
          <Link to="/club" style={{ color: "var(--teal-d)", fontSize: 13, fontWeight: 600,
                                     textDecoration: "underline", textUnderlineOffset: 3 }}>
            ← back to sign in
          </Link>
          <div className="kicker" style={{ marginTop: 14 }}>
            {config?.name || "Club Social®"} · Membership application
          </div>
          <h1 className="serif" style={{ fontSize: "clamp(28px, 4.5vw, 44px)", marginTop: 8, lineHeight: 1.1 }}>
            Apply for membership
          </h1>
          <p style={{ color: "var(--muted)", marginTop: 12, lineHeight: 1.6 }}>
            Fill out the form below. Our team will review your application and email you with your
            membership details. <b>Identity details must match your Aadhar</b> — used for verification and
            domicile-club eligibility.
          </p>

          {error && (
            <div data-testid="club-signup-error" style={{
              padding: "12px 14px", borderRadius: 10, marginTop: 18, fontSize: 13.5,
              background: "rgba(180,68,31,0.12)", color: "#7a2c12",
              border: "1px solid rgba(180,68,31,0.3)",
            }}>{error}</div>
          )}

          <form onSubmit={submit} style={{ marginTop: 26, display: "grid", gap: 18 }}>
            <Section title="Identity (as per Aadhar)">
              <Row>
                <FieldText label="First name *" value={draft.first_name} onChange={(v) => set("first_name", v)} testid="signup-first" />
                <FieldText label="Last name *" value={draft.last_name} onChange={(v) => set("last_name", v)} testid="signup-last" />
              </Row>
            </Section>

            <Section title="Contact">
              <Row>
                <FieldText label="Email *" type="email" value={draft.email} onChange={(v) => set("email", v)} testid="signup-email" />
                <FieldText label="Mobile *" value={draft.mobile} onChange={(v) => set("mobile", v)} testid="signup-mobile" />
              </Row>
              <Row>
                <FieldText label="Address" value={draft.address} onChange={(v) => set("address", v)} full multiline rows={2} testid="signup-address" />
              </Row>
            </Section>

            <Section title="Residence (as per Aadhar)">
              <Row>
                <FieldText label="City *" value={draft.resident_city} onChange={(v) => set("resident_city", v)} testid="signup-city" />
                <FieldText label="State" value={draft.resident_state} onChange={(v) => set("resident_state", v)} testid="signup-state" />
                <FieldText label="Pincode" value={draft.resident_pincode} onChange={(v) => set("resident_pincode", v)} testid="signup-pincode" />
              </Row>
            </Section>

            <Section title="Family (optional)">
              <Row>
                <FieldText label="Spouse name" value={draft.spouse_name} onChange={(v) => set("spouse_name", v)} testid="signup-spouse" />
              </Row>
              <Row>
                <div style={{ flex: "1 1 100%" }}>
                  <label style={labelStyle()}>Dependent children (max 2)</label>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                    {(draft.children_names || []).map((c, i) => (
                      <span key={i} style={{
                        background: "rgba(46,157,157,0.12)", color: "var(--teal-d)",
                        padding: "5px 10px", borderRadius: 100, fontSize: 12.5, fontWeight: 600,
                        display: "inline-flex", alignItems: "center", gap: 8,
                      }}>
                        {c}
                        <button type="button" onClick={() => removeChild(i)} aria-label="Remove"
                                style={{ background: "none", border: "none", color: "var(--teal-d)", cursor: "pointer", fontSize: 14 }}>×</button>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input style={input()} value={childInput}
                           onChange={(e) => setChildInput(e.target.value)}
                           onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addChild(); } }}
                           placeholder="Add child name + Enter"
                           data-testid="signup-child-input"
                           disabled={(draft.children_names || []).length >= 2} />
                    <button type="button" className="btn btn-ghost" style={{ padding: "10px 16px" }}
                            onClick={addChild} disabled={(draft.children_names || []).length >= 2}>
                      Add
                    </button>
                  </div>
                </div>
              </Row>
            </Section>

            {/* FIR-20260623 · founder requested photo upload on the signup form.
                 Photos are uploaded BEFORE the member row exists via the public
                 /club/signup/photo-upload helper, returning a Cloudinary URL
                 that's stored in form state and posted with the application. */}
            <Section title="Photographs (optional)">
              <Row>
                <PhotoField
                  label="Member photo"
                  slot="self"
                  url={draft.photo_url}
                  onChange={(url) => set("photo_url", url)}
                  testid="signup-photo-self"
                />
                <PhotoField
                  label="Spouse photo"
                  slot="spouse"
                  url={draft.spouse_photo_url}
                  onChange={(url) => set("spouse_photo_url", url)}
                  testid="signup-photo-spouse"
                />
              </Row>
            </Section>

            {/* FIR-20260612-007 · DPDP Act 2023 consent panel.
                 Notice + two ticks, both pre-ticked by default per
                 founder direction (12 Feb 2026). Required tick gates
                 submit; marketing tick is fully optional. Both are
                 echoed into the Zoho Desk ticket for audit trail.
                 Compact 11-12px typography per founder request. */}
            <div style={{ padding: "12px 14px",
                          background: "rgba(35,48,42,0.04)", border: "1px solid var(--line-2)",
                          borderRadius: 10, fontSize: 11, lineHeight: 1.55, color: "var(--ink)" }}
                 data-testid="signup-dpdp-panel">
              <p style={{ margin: 0, fontWeight: 600, fontSize: 11, letterSpacing: "0.06em",
                          textTransform: "uppercase", color: "var(--muted)" }}>
                How we'll use your information
              </p>
              <p style={{ margin: "6px 0 0", fontSize: 11 }}>
                <strong>MindEscapes</strong> is the Data Fiduciary for this application.
                We collect the details on this form — your name, contact details, residential
                address, identity reference, and (if you choose to add them) your spouse and
                children's names and photographs — for the following purposes:
              </p>
              <ul style={{ margin: "4px 0 0 16px", padding: 0, fontSize: 11 }}>
                <li><strong>Membership verification &amp; onboarding</strong> — confirming your
                  identity, allocating your card and opening access to the Club Discovery Network.</li>
                <li><strong>Ongoing member services</strong> — honouring reservations, agreement
                  obligations and assistance you request.</li>
                <li><strong>Statutory record-keeping</strong> — Indian tax and corporate law
                  (typically retained for seven years after your membership ends, then permanently
                  erased).</li>
              </ul>
              <p style={{ margin: "6px 0 0", fontSize: 11 }}>
                <strong>Your rights as a Data Principal:</strong> access the personal data we hold,
                ask us to correct or erase it, nominate someone to exercise your rights, and
                withdraw your consent at any time — write to
                {" "}<a href="mailto:privacy@mindescapes.in" style={{ color: "var(--brass)" }}>
                  privacy@mindescapes.in
                </a>. Withdrawing consent will end your membership but will not affect anything we
                lawfully did before withdrawal. For grievances, write to the same address. If
                unresolved, you may approach the <strong>Data Protection Board of India</strong>
                {" "}under the Digital Personal Data Protection Act, 2023.
              </p>

              {/* Required consent · pre-ticked */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8,
                            marginTop: 10, paddingTop: 10,
                            borderTop: "1px dashed var(--line-2)" }}>
                <input type="checkbox" id="dpdp" checked={draft.dpdp_consent}
                       onChange={(e) => set("dpdp_consent", e.target.checked)}
                       data-testid="signup-dpdp-consent"
                       style={{ marginTop: 2 }} />
                <label htmlFor="dpdp" style={{ fontSize: 11, lineHeight: 1.5 }}>
                  I am 18 years or older. I have read the notice above and{" "}
                  <strong>consent</strong> to MindEscapes processing my personal data —
                  including the identity, spouse and family details I have provided — for
                  membership verification, onboarding and ongoing services. I confirm the
                  details I have entered match my official identity proof.
                  <span style={{ color: "var(--brass)" }}> (required)</span>
                </label>
              </div>

              {/* Marketing consent · pre-ticked, optional */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8,
                            marginTop: 8 }}>
                <input type="checkbox" id="marketing" checked={draft.marketing_consent}
                       onChange={(e) => set("marketing_consent", e.target.checked)}
                       data-testid="signup-marketing-consent"
                       style={{ marginTop: 2 }} />
                <label htmlFor="marketing" style={{ fontSize: 11, lineHeight: 1.5 }}>
                  I'd also like to hear from MindEscapes about new partner clubs,
                  member-only experiences and seasonal programmes. You can opt out at any
                  time from any email or by writing to privacy@mindescapes.in.
                  <span style={{ color: "var(--muted)" }}> (optional · uncheck if you'd rather not)</span>
                </label>
              </div>
            </div>

            <button type="submit" className="btn btn-primary"
                    disabled={submitting} style={{ width: "100%", padding: "14px" }}
                    data-testid="signup-submit-btn">
              {submitting ? "Submitting…" : "Submit application"}
            </button>
          </form>
        </div>
      </main>
    </ClubLayout>
  );
}

/* ─── Inline micro-components ──────────────────────────────────────── */

function Section({ title, children }) {
  return (
    <div>
      <h4 style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase",
                   color: "var(--muted)", fontWeight: 700, margin: "0 0 12px" }}>
        {title}
      </h4>
      {children}
    </div>
  );
}
function Row({ children }) {
  return <div style={{ display: "flex", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>{children}</div>;
}
function labelStyle() {
  return { fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase",
           color: "var(--muted)", fontWeight: 700, marginBottom: 4, display: "block" };
}
function input() {
  return { border: "1px solid var(--line)", borderRadius: 10, background: "var(--bg)",
           padding: "12px 14px", fontFamily: "'Archivo', sans-serif", fontSize: 14.5,
           color: "var(--ink)", width: "100%", boxSizing: "border-box" };
}
function FieldText({ label, value, onChange, type = "text", testid, full, multiline, rows = 1 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: full ? "1 1 100%" : "1 1 200px", minWidth: 0 }}>
      <label style={labelStyle()}>{label}</label>
      {multiline
        ? <textarea value={value || ""} rows={rows} onChange={(e) => onChange(e.target.value)} style={input()} data-testid={testid} />
        : <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} style={input()} data-testid={testid} />}
    </div>
  );
}


/** Public photo uploader for the signup form. POSTs to
 *  /api/club/signup/photo-upload (no auth) — returns a Cloudinary URL
 *  that the caller stores in form state and submits with the application.
 */
function PhotoField({ label, slot, url, onChange, testid }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  async function pick(file) {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { setErr("Please choose an image"); return; }
    if (file.size > 5 * 1024 * 1024) { setErr("Max 5 MB"); return; }
    setErr(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/club/signup/photo-upload?slot=${slot}`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json();
      if (!res.ok || !data?.url) {
        setErr(data?.detail || "Upload failed");
        return;
      }
      onChange(data.url);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: "1 1 220px", minWidth: 0 }}>
      <label style={labelStyle()}>{label}</label>
      <div style={{
        display: "flex", alignItems: "center", gap: 12, padding: "10px 12px",
        border: "1px solid var(--line)", borderRadius: 10, background: "var(--bg)",
      }}>
        {url ? (
          <CldImg src={url} w={112} alt={label}
               style={{ width: 56, height: 56, borderRadius: 8, objectFit: "cover", border: "1px solid var(--line-2)" }} />
        ) : (
          <div style={{ width: 56, height: 56, borderRadius: 8, background: "var(--line-2)",
                        display: "grid", placeItems: "center", fontSize: 18, color: "var(--muted)" }}>+</div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
          <button type="button" className="btn btn-ghost"
                  style={{ padding: "6px 10px", fontSize: 12, alignSelf: "flex-start" }}
                  onClick={() => inputRef.current?.click()} disabled={busy}
                  data-testid={`${testid}-pick`}>
            {busy ? "Uploading…" : url ? "Replace photo" : "Choose photo"}
          </button>
          {url && (
            <button type="button" className="btn-link"
                    style={{ padding: 0, fontSize: 11, color: "var(--muted)", alignSelf: "flex-start",
                             background: "none", border: "none", cursor: "pointer" }}
                    onClick={() => onChange("")} data-testid={`${testid}-remove`}>
              Remove
            </button>
          )}
          {err && <span style={{ fontSize: 11, color: "#b03a3a" }}>{err}</span>}
        </div>
        <input ref={inputRef} type="file" accept="image/*" hidden
               onChange={(e) => pick(e.target.files?.[0])}
               data-testid={`${testid}-input`} />
      </div>
    </div>
  );
}
